import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = '/api';

const DynamicNavigation = ({ isMobile = false, onMenuToggle, activeMenu, menuRefs }) => {
    const [navigationItems, setNavigationItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchNavigationItems();
    }, []);

    const fetchNavigationItems = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/navigation/`);
            setNavigationItems(response.data.results || response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching navigation:', err);
            setError('Failed to load navigation');
            // Fallback to static navigation items
            setNavigationItems(getStaticNavigationItems());
        } finally {
            setLoading(false);
        }
    };

    // Fallback static navigation items
    const getStaticNavigationItems = () => {
        return [
            { id: 1, title: 'Home', url: '/', link_type: 'internal', children: [] },
            { id: 2, title: 'About Us', url: '/about', link_type: 'internal', children: [] },
            { id: 3, title: 'Destination', url: '/destination', link_type: 'internal', children: [] },
            { id: 4, title: 'Service', url: '/service', link_type: 'internal', children: [] },
            { id: 5, title: 'Activities', url: '/activities', link_type: 'internal', children: [] },
            { 
                id: 6, 
                title: 'Pages', 
                url: '', 
                link_type: 'dropdown', 
                children: [
                    { id: 10, title: 'Tour Guide', url: '/tour-guide', link_type: 'internal' },
                    { id: 11, title: 'Gallery', url: '/gallery', link_type: 'internal' },
                    { id: 12, title: 'FAQ', url: '/faq', link_type: 'internal' },
                    { id: 13, title: 'Pricing', url: '/pricing', link_type: 'internal' }
                ]
            },
            { id: 7, title: 'Blog', url: '/blog', link_type: 'internal', children: [] },
            { id: 8, title: 'Contact Us', url: '/contact', link_type: 'internal', children: [] },
        ];
    };

    const renderDesktopMenu = () => {
        if (loading) return <li><span>Loading...</span></li>;
        
        return navigationItems.map((item, index) => {
            const hasChildren = item.children && item.children.length > 0;
            
            if (item.title === 'Home') {
                // Special handling for Home menu with mega menu
                return (
                    <li key={item.id} className="menu-item-has-children mega-menu-wrap">
                        <Link className="active" to={item.url}>
                            {item.title}
                        </Link>
                        <ul className="mega-menu mega-menu-content">
                            <li>
                                <div className="container">
                                    <div className="row gy-4">
                                        <div className="col-lg-3">
                                            <div className="mega-menu-box">
                                                <div className="mega-menu-img">
                                                    <img src="/assets/img/pages/home-travel.jpg" alt="Home One" />
                                                    <div className="btn-wrap">
                                                        <Link to="/" className="th-btn">View Demo</Link>
                                                    </div>
                                                </div>
                                                <h3 className="mega-menu-title">
                                                    <Link to="/"><span>01.</span>Home Travel</Link>
                                                </h3>
                                            </div>
                                        </div>
                                        <div className="col-lg-3">
                                            <div className="mega-menu-box">
                                                <div className="mega-menu-img">
                                                    <img src="/assets/img/pages/home-tour.jpg" alt="Home Two" />
                                                    <div className="btn-wrap">
                                                        <Link to="/home-tour" className="th-btn">View Demo</Link>
                                                    </div>
                                                </div>
                                                <h3 className="mega-menu-title">
                                                    <Link to="/home-tour"><span>02.</span>Home Tour</Link>
                                                </h3>
                                            </div>
                                        </div>
                                        <div className="col-lg-3">
                                            <div className="mega-menu-box">
                                                <div className="mega-menu-img">
                                                    <img src="/assets/img/pages/home-agency.jpg" alt="Home Three" />
                                                    <div className="btn-wrap">
                                                        <Link to="/home-agency" className="th-btn">View Demo</Link>
                                                    </div>
                                                </div>
                                                <h3 className="mega-menu-title">
                                                    <Link to="/home-agency"><span>03.</span>Home Agency</Link>
                                                </h3>
                                            </div>
                                        </div>
                                        <div className="col-lg-3">
                                            <div className="mega-menu-box">
                                                <div className="mega-menu-img">
                                                    <img src="/assets/img/pages/home-yacht.jpg" alt="Home Four" />
                                                    <div className="btn-wrap">
                                                        <Link to="/home-yacht" className="th-btn">View Demo</Link>
                                                    </div>
                                                </div>
                                                <h3 className="mega-menu-title">
                                                    <Link to="/home-yacht"><span>04.</span>Home Yacht</Link>
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </li>
                );
            }
            
            if (hasChildren) {
                return (
                    <li key={item.id} className="menu-item-has-children">
                        <Link to={item.url || '#'}>{item.title}</Link>
                        <ul className="sub-menu">
                            {item.children.map(child => (
                                <li key={child.id}>
                                    <Link to={child.url}>{child.title}</Link>
                                </li>
                            ))}
                        </ul>
                    </li>
                );
            }
            
            return (
                <li key={item.id}>
                    <Link to={item.url}>{item.title}</Link>
                </li>
            );
        });
    };

    const renderMobileMenu = () => {
        if (loading) return <li><span>Loading...</span></li>;
        
        return navigationItems.map((item, index) => {
            const hasChildren = item.children && item.children.length > 0;
            const menuIndex = index + 1;
            
            if (item.title === 'Home') {
                // Special handling for Home menu
                return (
                    <li key={item.id} className={`menu-item-has-children mega-menu-wrap th-item-has-children ${activeMenu === menuIndex ? "th-active" : ""}`}>
                        <Link to="#" onClick={() => onMenuToggle(menuIndex)}>
                            {item.title}
                        </Link>
                        <ul 
                            ref={menuRefs ? (el) => (menuRefs.current[menuIndex] = el) : null}
                            className="th-submenu" 
                            style={{ height: "0px", overflow: "hidden", transition: "height 0.3s ease-in-out" }}
                        >
                            <li><Link to="/">Home Travel</Link></li>
                            <li><Link to="/home-tour">Home Tour</Link></li>
                            <li><Link to="/home-agency">Home Agency</Link></li>
                            <li><Link to="/home-yacht">Home Yacht</Link></li>
                        </ul>
                    </li>
                );
            }
            
            if (hasChildren) {
                return (
                    <li key={item.id} className={`menu-item-has-children th-item-has-children ${activeMenu === menuIndex ? "th-active" : ""}`}>
                        <Link to="#" onClick={() => onMenuToggle(menuIndex)}>
                            {item.title}
                        </Link>
                        <ul 
                            ref={menuRefs ? (el) => (menuRefs.current[menuIndex] = el) : null}
                            className="th-submenu" 
                            style={{ height: "0px", overflow: "hidden", transition: "height 0.3s ease-in-out" }}
                        >
                            {item.children.map(child => (
                                <li key={child.id}>
                                    <Link to={child.url}>{child.title}</Link>
                                </li>
                            ))}
                        </ul>
                    </li>
                );
            }
            
            return (
                <li key={item.id}>
                    <Link to={item.url}>{item.title}</Link>
                </li>
            );
        });
    };

    if (error && loading) {
        return isMobile ? <li><span>Loading navigation...</span></li> : <span>Loading navigation...</span>;
    }

    return isMobile ? renderMobileMenu() : renderDesktopMenu();
};

export default DynamicNavigation;
